import type { Locale } from '@/i18n/config'

/**
 * Persistence keys for the UI locale. These MUST stay in sync with the
 * i18next-browser-languagedetector config in `src/spa/i18n/index.ts`:
 *   - localStorage uses the detector's default `i18nextLng` key
 *   - cookie uses `NEXT_LOCALE` (the detector's `lookupCookie`)
 */
export const LOCALE_STORAGE_KEY = 'i18nextLng'
export const LOCALE_COOKIE_KEY = 'NEXT_LOCALE'

/**
 * Persist the chosen UI locale so it survives the full document reload the
 * language switcher triggers (see `LanguageSelector`).
 *
 * Why BOTH stores: on iOS the app runs from the `capacitor://localhost` custom
 * scheme, where `document.cookie` is NOT reliably persisted across a hard
 * reload — the same WKWebView limitation that moved the Supabase session from
 * cookies to localStorage. localStorage survives the reload, so it's the real
 * source of truth on native; the cookie is kept for web/SSR back-compat (and is
 * harmless on native). Without the localStorage write, the boot-time detector
 * found no cookie after a native reload and fell through to `navigator` — i.e.
 * the device language — which is exactly the "can't switch language" bug.
 */
export function persistLocale(locale: Locale): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // localStorage can throw in private mode / when storage is disabled — the
    // cookie write below is the fallback so we don't lose the choice entirely.
  }
  if (typeof document !== 'undefined') {
    document.cookie = `${LOCALE_COOKIE_KEY}=${locale};path=/;max-age=31536000;samesite=lax`
  }
}
